import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Heart, ArrowLeft, Filter, List, Map as MapIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import MapSearchView from '@/components/MapSearchView';
import PetCard from '@/components/PetCard';
import Pagination from '@/components/Pagination';
import { PET_COLORS } from '@/utils/petColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 24;
const MAP_FETCH_LIMIT = 100; // map view pulls a wider window since the user filters by geo

const LostPets = () => {
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, lost: 0, found: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({ petType: '', color: '', dateFrom: '', dateTo: '' });
  const [viewMode, setViewMode] = useState('list');
  const [page, setPage] = useState(0);

  // Build the filter query params shared by /pets/all and /pets/stats
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.petType) params.append('pet_type', filters.petType);
    if (filters.color) params.append('color', filters.color);
    if (filters.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters.dateTo) params.append('date_to', filters.dateTo);
    return params;
  }, [filters]);

  const loadPets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildFilterParams();
      if (activeTab !== 'all') params.append('type', activeTab);
      if (viewMode === 'map') {
        params.append('skip', '0');
        params.append('limit', String(MAP_FETCH_LIMIT));
      } else {
        params.append('skip', String(page * PAGE_SIZE));
        params.append('limit', String(PAGE_SIZE));
      }
      const res = await axios.get(`${API}/pets/all?${params.toString()}`, { timeout: 30000 });
      setPets(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Error loading pets:', err);
      setError('Error al cargar mascotas. Intenta recargar la página.');
      toast.error('Error al cargar mascotas');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, viewMode, buildFilterParams]);

  const loadStats = useCallback(async () => {
    try {
      const params = buildFilterParams();
      const res = await axios.get(`${API}/pets/stats?${params.toString()}`, { timeout: 30000 });
      setStats(res.data || { total: 0, lost: 0, found: 0 });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, [buildFilterParams]);

  // Reset to page 0 whenever filters or tab change
  useEffect(() => {
    setPage(0);
  }, [filters, activeTab, viewMode]);

  useEffect(() => {
    loadPets();
  }, [loadPets]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const counts = {
    all: stats.total,
    lost: stats.lost,
    found: stats.found,
  };

  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const filtersActive = filters.petType || filters.color || filters.dateFrom || filters.dateTo;

  return (
    <div className="min-h-screen gradient-mesh">
      <header className="bg-white/80 backdrop-blur-sm border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))} className="rounded-full shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Heart className="h-6 w-6 text-primary shrink-0" fill="currentColor" />
          <h1 className="text-lg sm:text-xl font-fraunces font-semibold truncate">Mascotas Perdidas y Encontradas</h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Toggle Lista / Mapa */}
        <div className="flex justify-center mb-6">
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={(v) => v && setViewMode(v)}
            className="bg-white/80 backdrop-blur-sm rounded-full p-1 border border-border"
          >
            <ToggleGroupItem value="list" className="rounded-full px-4 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
              <List className="h-4 w-4 mr-2" />
              Lista
            </ToggleGroupItem>
            <ToggleGroupItem value="map" className="rounded-full px-4 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
              <MapIcon className="h-4 w-4 mr-2" />
              Mapa
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {/* Filtros */}
        <Card className="rounded-3xl border-border mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" /> Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <Select value={filters.petType} onValueChange={(v) => setFilters({ ...filters, petType: v })}>
                <SelectTrigger className="w-full sm:w-40 rounded-xl">
                  <SelectValue placeholder="Tipo de animal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dog">🐕 Perro</SelectItem>
                  <SelectItem value="cat">🐈 Gato</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.color} onValueChange={(v) => setFilters({ ...filters, color: v })}>
                <SelectTrigger className="w-full sm:w-44 rounded-xl">
                  <SelectValue placeholder="Color" />
                </SelectTrigger>
                <SelectContent>
                  {PET_COLORS.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <label className="text-sm text-muted-foreground whitespace-nowrap">Desde</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="flex-1 sm:w-40 rounded-xl"
                />
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <label className="text-sm text-muted-foreground whitespace-nowrap">Hasta</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="flex-1 sm:w-40 rounded-xl"
                />
              </div>
              {filtersActive && (
                <Button variant="outline" size="sm" onClick={() => setFilters({ petType: '', color: '', dateFrom: '', dateTo: '' })} className="rounded-full w-full sm:w-auto">
                  Limpiar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs (status filter — compartido por lista y mapa) */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 rounded-full">
            <TabsTrigger value="all" className="rounded-full text-xs sm:text-sm px-1.5 sm:px-3">Todas ({counts.all})</TabsTrigger>
            <TabsTrigger value="lost" className="rounded-full text-xs sm:text-sm px-1.5 sm:px-3">Perdidas ({counts.lost})</TabsTrigger>
            <TabsTrigger value="found" className="rounded-full text-xs sm:text-sm px-1.5 sm:px-3">Encontradas ({counts.found})</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Contenido */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">Cargando...</p>
          </div>
        ) : error ? (
          <Card className="rounded-3xl">
            <CardContent className="py-12 text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={loadPets}>Reintentar</Button>
            </CardContent>
          </Card>
        ) : viewMode === 'map' ? (
          <MapSearchView
            pets={pets}
            petTypeFilter={filters.petType}
            statusFilter={activeTab}
            onSelectPet={(petId) => navigate(`/pet/${petId}`)}
          />
        ) : pets.length === 0 ? (
          <Card className="rounded-3xl">
            <CardContent className="py-12 text-center">
              <p className="text-4xl mb-4">🔍</p>
              <p className="text-muted-foreground">No se encontraron mascotas</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pets.map((pet) => (
                <PetCard
                  key={pet.pet_id}
                  pet={pet}
                  onClick={() => navigate(`/pet/${pet.pet_id}`)}
                />
              ))}
            </div>

            {/* Paginación */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2 mt-8">
              <p className="text-sm text-muted-foreground">
                {total} mascota(s) — Página {page + 1} de {maxPage + 1}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <Pagination page={page} totalPages={maxPage + 1} onPageChange={setPage} />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={page >= maxPage}
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LostPets;
